import { useState, useEffect } from "react";

export type Client = {
  id: string;
  full_name: string;
};

export type Contract = {
  id: string;
  title: string;
  contract_number: string;
  total_amount: number;
  balance_amount: number;
  status: string;
  payment_status?: string;
};

export type Job = {
  id: string;
  tenant_id: string;
  client_id: string;
  contract_id: string | null;
  title: string;
  job_type: string;
  status: string;
  workflow_status: string;
  shoot_date: string | null;
  location: string | null;
  notes: string | null;
  calendar_event_id: string | null;
  calendar_sync_status: string | null;
  photo_selection_status: string | null;
  photo_selection_submitted_at: string | null;
  delivery_status: string | null;
  delivered_at: string | null;
  editing_started_at: string | null;
  completed_at: string | null;
  portal_token: string | null;
  created_at: string;
  updated_at: string;
};

const JOB_STATUSES = [
  { value: "scheduled",     label: "Scheduled" },
  { value: "confirmed",     label: "Confirmed" },
  { value: "shooting",      label: "Shooting" },
  { value: "editing",       label: "Editing" },
  { value: "waiting_client",label: "Waiting Client" },
  { value: "rendering",     label: "Rendering" },
  { value: "delivered",     label: "Delivered" },
  { value: "completed",     label: "Completed" },
  { value: "cancelled",     label: "Cancelled" },
];

const JOB_TYPES = [
  "Wedding",
  "Portrait",
  "Engagement",
  "Family",
  "Newborn",
  "Event",
  "Corporate",
  "Product",
  "Real Estate",
  "Boudoir",
  "Other",
];

interface Props {
  job: Job | null;
  isNew: boolean;
  clients: Client[];
  contracts: Contract[];
  onClose: () => void;
  onSave: (data: Job) => void;
}

const emptyForm = (): Job => ({
  id: "",
  tenant_id: "",
  client_id: "",
  contract_id: null,
  title: "",
  job_type: "Wedding",
  status: "scheduled",
  workflow_status: "scheduled",
  shoot_date: null,
  location: null,
  notes: null,
  calendar_event_id: null,
  calendar_sync_status: "not_synced",
  photo_selection_status: "not_started",
  photo_selection_submitted_at: null,
  delivery_status: "not_started",
  delivered_at: null,
  editing_started_at: null,
  completed_at: null,
  portal_token: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

export default function JobModal({ job, isNew, clients, contracts, onClose, onSave }: Props) {
  const [form, setForm] = useState<Job>(emptyForm());

  useEffect(() => {
    if (job) {
      setForm(job);
    } else {
      setForm(emptyForm());
    }
  }, [job]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...form, updated_at: new Date().toISOString() });
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-[#0f172a] font-semibold text-base">
            {isNew ? "New Job / Shoot" : "Edit Job / Shoot"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            {/* Title */}
            <div className="col-span-2">
              <label className="text-slate-400 text-xs mb-1.5 block">Job Title *</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
                placeholder="e.g. Johnson Wedding — Full Day"
              />
            </div>

            {/* Client */}

            <div>
              <label className="text-slate-400 text-xs mb-1.5 block">Client *</label>
              <select
                name="client_id"
                value={form.client_id}
                onChange={handleChange}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              >
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Contract (optional) */}
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block">
                Contract <span className="text-slate-300">(optional)</span>
              </label>
              <select
                name="contract_id"
                value={form.contract_id ?? ""}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              >
                <option value="">No contract linked</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Job Type */}
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block">Job Type *</label>
              <select
                name="job_type"
                value={form.job_type}
                onChange={handleChange}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              >
                {JOB_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Workflow Status */}
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block">Workflow Status *</label>
              <select
                name="workflow_status"
                value={form.workflow_status || form.status}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((prev) => ({ ...prev, workflow_status: val, status: val }));
                }}
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              >
                {JOB_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Shoot Date */}
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block">Shoot Date</label>
              <input
                name="shoot_date"
                type="datetime-local"
                value={form.shoot_date ? form.shoot_date.slice(0, 16) : ""}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>

            {/* Location */}
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block">Location</label>
              <input
                name="location"
                value={form.location ?? ""}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
                placeholder="Venue or address"
              />
            </div>

            {/* Notes */}
            <div className="col-span-2">
              <label className="text-slate-400 text-xs mb-1.5 block">Notes</label>
              <textarea
                name="notes"
                value={form.notes ?? ""}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
                placeholder="Internal notes about this job…"
              />
            </div>

            {/* Calendar sync status — read only */}
            {!isNew && (
              <div className="col-span-2">
                <div className="flex items-center justify-between px-3.5 py-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                        form.calendar_sync_status === "synced"
                          ? "bg-emerald-500/15"
                          : form.calendar_sync_status === "sync_error"
                          ? "bg-red-500/15"
                          : "bg-white/5"
                      }`}
                    >
                      <i
                        className={`text-xs ${
                          form.calendar_sync_status === "synced"
                            ? "ri-calendar-check-line text-emerald-400"
                            : form.calendar_sync_status === "sync_error"
                            ? "ri-calendar-close-line text-red-400"
                            : "ri-calendar-line text-slate-300"
                        }`}
                      />
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs font-medium">Google Calendar</p>
                      <p
                        className={`text-[11px] capitalize ${
                          form.calendar_sync_status === "synced"
                            ? "text-emerald-400"
                            : form.calendar_sync_status === "sync_error"
                            ? "text-red-400"
                            : "text-slate-300"
                        }`}
                      >
                        {form.calendar_sync_status === "not_synced"
                          ? "Not synced — backend pending"
                          : form.calendar_sync_status?.replace("_", " ") ?? "Not synced"}
                      </p>
                    </div>
                  </div>
                  {form.calendar_event_id && (
                    <span className="text-slate-300 text-[10px] font-mono truncate max-w-[120px]">
                      {form.calendar_event_id.slice(0, 8)}…
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965e] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? "Create Job" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
